import React, { memo, useMemo, useState } from "react";
import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Typography,
  TableSortLabel,
  Box,
} from "@mui/material";

interface RDTColumn {
  name: string;
  selector: (row: any) => React.ReactNode;
  width?: string;
  sortable?: boolean;
  onCellClick?: (row: any) => void;
}

interface TableProps {
  columns: RDTColumn[];
  data: any[];
  className?: string;
  isBackendPagination?: boolean;
  paginationProps?: {
    isPagination: boolean;
    totalCount: number;
    pageSelected: number;
    setPageSelected: (page: number) => void;
    rowsPerPageValue?: number;
    setRowsPerPageValue: (rows: number) => void;
  };
}

const Table: React.FC<TableProps> = ({
  columns,
  data,
  className,
  paginationProps,
  isBackendPagination = true,
}) => {
  const [rowsPerPage, setRowsPerPage] = useState(
    paginationProps?.rowsPerPageValue || 10
  );

  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Sorting logic
  const handleSort = (col: RDTColumn) => {
    if (!col.sortable) return;
    if (sortColumn === col.name)
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    else {
      setSortColumn(col.name);
      setSortOrder("asc");
    }
  };

  const sortedData = useMemo(() => {
    if (!sortColumn) return data;
    const col = columns.find((c) => c.name === sortColumn);
    if (!col) return data;

    return [...data].sort((a, b) => {
      let valA = col.selector(a);
      let valB = col.selector(b);
      if (valA == null) valA = "";
      if (valB == null) valB = "";

      if (typeof valA !== "number") valA = String(valA).toLowerCase();
      if (typeof valB !== "number") valB = String(valB).toLowerCase();

      if (valA < valB) return sortOrder === "asc" ? -1 : 1;
      if (valA > valB) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortColumn, sortOrder, columns]);

  const currentPage = paginationProps?.pageSelected || 1;

  const paginatedData = useMemo(() => {
    if (isBackendPagination) return sortedData;
    const start = (currentPage - 1) * rowsPerPage;
    return sortedData.slice(start, start + rowsPerPage);
  }, [sortedData, currentPage, rowsPerPage, isBackendPagination]);

  const handleChangePage = (_: unknown, newPage: number) => {
    paginationProps?.setPageSelected(
      isBackendPagination ? newPage + 1 : newPage
    );
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newValue = parseInt(event.target.value);
    setRowsPerPage(newValue);
    paginationProps?.setRowsPerPageValue?.(newValue);
    paginationProps?.setPageSelected(1);
  };

  return (
    <Box sx={{ width: "100%" }} className={className}>
      <TableContainer component={Paper} sx={{ width: "100%",overflowY: "auto",maxHeight: "calc(100vh - 250px)" }}>
        <MuiTable stickyHeader sx={{ tableLayout: "auto", width: "100%" }}>
          {/* Table Head */}
          <TableHead>
            <TableRow>
              {columns.map((col, index) => (
                <TableCell
                  key={index}
                  sx={{
                    width: col.width || "auto",
                    fontWeight: "bold",
                    backgroundColor: "#EFF1FE",
                  }}
                >
                  {col.sortable ? (
                    <TableSortLabel
                      active={sortColumn === col.name}
                      direction={sortColumn === col.name ? sortOrder : "asc"}
                      onClick={() => handleSort(col)}
                    >
                      {col.name}
                    </TableSortLabel>
                  ) : (
                    col.name
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {/* Table Body */}
          <TableBody>
            {paginatedData.length > 0 ? (
                paginatedData.map((row, rowIndex) => (
                  <TableRow
                    key={rowIndex}
                    sx={{
                      backgroundColor: row.isGroupHeader ? "#f0f0f0" : "inherit",
                    }}
                  >
                    {columns.map((col, colIndex) => (
                      <TableCell
                        key={colIndex}
                        colSpan={row.isGroupHeader ? columns.length : 1}
                        sx={{
                          fontWeight: row.isGroupHeader ? "bold" : "normal",
                          textAlign: row.isGroupHeader ? "left" : "inherit",
                        }}
                      >
                        {row.isGroupHeader
                          ? row.warehouse
                          : col.selector(row)
                        }
                      </TableCell>
                    ))}
                  </TableRow>
                ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Typography color="textSecondary" sx={{ py: 3 }}>
                    No Records Found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </MuiTable>
      </TableContainer>

      {/* Pagination */}
      {paginationProps?.isPagination && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={paginationProps.totalCount || data.length}
          rowsPerPage={rowsPerPage}
          page={isBackendPagination ? currentPage - 1 : currentPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </Box>
  );
};

export default memo(Table);
